import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useActiveOrganization } from '@/graphql/queries/organizations';
import { FinalReview, type ClauseFinding, type FinalReviewData } from '../components/final-review';
import { useGetContractAnalysisById } from '../hooks/use-contract-analyses';

// Recompute risk counts for an arbitrary (selected) subset of findings so the
// downloaded PDF's summary reflects exactly what's included.
const countByRisk = (findings: ClauseFinding[]) => {
  let high = 0;
  let medium = 0;
  for (const f of findings) {
    if (f.risk === 'high') high++;
    else if (f.risk === 'medium') medium++;
  }
  return { high, medium };
};

/**
 * Org route: `/org/<slug>/contract-checker/result/:id`
 *
 * Loads a persisted analysis from UDS and renders it via `<FinalReview>`.
 * The findings the AI flagged as relevant to the user's Topics (entered at
 * the upload/setup step) are stored as `MatchedFindingIds` and drive the
 * "User specific query" tab. Inline edits stay local (download only).
 */
const OrgContractCheckerResultPage = () => {
  const { orgId: slug, id } = useParams();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: org, isLoading: isOrgLoading } = useActiveOrganization();
  const { data: analysis, isLoading, error } = useGetContractAnalysisById(id ?? '');

  const finalReviewData = useMemo<FinalReviewData | null>(() => {
    if (!analysis) return null;
    let findings: ClauseFinding[] = [];
    try {
      findings = JSON.parse(analysis.Findings) as ClauseFinding[];
    } catch (err) {
      console.error('[contract-checker] Failed to parse stored Findings', err);
    }
    return {
      projectName: analysis.ProjectName,
      totalPoints: analysis.TotalPoints,
      highRiskCount: analysis.HighRiskCount,
      mediumRiskCount: analysis.MediumRiskCount,
      findings,
    };
  }, [analysis]);

  // Ids the AI flagged as matching the user's Topics query, persisted on the
  // row at analysis time. `null` = legacy row with no stored matches.
  const matchedIds = useMemo<string[] | null>(() => {
    if (!analysis?.MatchedFindingIds) return null;
    try {
      const ids = JSON.parse(analysis.MatchedFindingIds) as unknown;
      return Array.isArray(ids) ? (ids as string[]) : null;
    } catch {
      return null;
    }
  }, [analysis]);

  // Lazy-load the PDF helper so the ~2 MB `@react-pdf/renderer` runtime
  // only enters the bundle when the user actually clicks Download. Counts
  // are recomputed from the selected subset so the summary matches.
  const handleDownload = useCallback(
    async (selectedFindings: ClauseFinding[]) => {
      if (!finalReviewData) return;
      const { high, medium } = countByRisk(selectedFindings);
      try {
        const { downloadContractReviewPdf } = await import('../lib/download-contract-review-pdf');
        await downloadContractReviewPdf({
          ...finalReviewData,
          findings: selectedFindings,
          totalPoints: selectedFindings.length,
          highRiskCount: high,
          mediumRiskCount: medium,
        });
      } catch (err) {
        console.error('[contract-checker] download failed', err);
        toast({
          variant: 'destructive',
          title: t('ORGCONTRACTCHECKER_RESULT_DOWNLOAD_ERROR', {
            defaultValue: 'Could not generate the review PDF. Please try again.',
          }),
        });
      }
    },
    [finalReviewData, t, toast]
  );

  if (isOrgLoading || isLoading) return <Skeleton className="h-150 w-full" />;
  if (!org || !slug || !id) return null;

  // Unknown / deleted id → bounce back to the list.
  if (!analysis && !error) {
    return <Navigate to={`/org/${slug}/contract-checker`} replace />;
  }

  if (!finalReviewData) return null;

  return (
    <FinalReview
      data={finalReviewData}
      backHref={`/org/${slug}/contract-checker`}
      onDownload={handleDownload}
      query={analysis?.Topics}
      matchedIds={matchedIds}
    />
  );
};

export default OrgContractCheckerResultPage;
